import type { Platform } from '@multimod/shared';
import type { ChatEvent } from '@multimod/shared';
import type { ChatFeedStore } from './store.js';

const BASE = 1000;
const CAP = 32000;
const MAX_ATTEMPTS = 10;

function delayFor(attempt: number): number {
  return Math.min(BASE * 2 ** attempt, CAP);
}

export type WsMode =
  | { kind: 'primary'; platform: Platform; liveChatId: string }
  | {
      kind: 'attach';
      primaryPlatform: Platform;
      primaryLiveChatId: string;
      attachPlatform: Platform;
      attachExternalId: string;
      attachLiveChatId: string;
    };

export class ChatWsClient {
  private ws: WebSocket | null = null;
  private attempts = 0;
  private terminated = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly mode: WsMode,
    private readonly store: ChatFeedStore,
    private readonly WSCtor: typeof WebSocket = WebSocket,
  ) {}

  private get activePlatform(): Platform {
    return this.mode.kind === 'primary' ? this.mode.platform : this.mode.attachPlatform;
  }

  private get activeLiveChatId(): string {
    return this.mode.kind === 'primary' ? this.mode.liveChatId : this.mode.attachLiveChatId;
  }

  private buildUrl(): string {
    const loc = (globalThis as { location?: { protocol: string; host: string } }).location;
    const scheme = loc?.protocol === 'https:' ? 'wss' : 'ws';
    const host = loc?.host ?? 'localhost';
    if (this.mode.kind === 'primary') {
      return (
        `${scheme}://${host}/chat/connect` +
        `?liveChatId=${encodeURIComponent(this.mode.liveChatId)}` +
        `&platform=${this.mode.platform}`
      );
    }
    return (
      `${scheme}://${host}/chat/connect` +
      `?attachPlatform=${this.mode.attachPlatform}` +
      `&attachExternalId=${encodeURIComponent(this.mode.attachExternalId)}` +
      `&attachLiveChatId=${encodeURIComponent(this.mode.attachLiveChatId)}` +
      `&liveChatId=${encodeURIComponent(this.mode.primaryLiveChatId)}` +
      `&platform=${this.mode.primaryPlatform}`
    );
  }

  connect(): void {
    if (this.terminated) return;
    this.store.getState().push({
      type: 'connection',
      platform: this.activePlatform,
      liveChatId: this.activeLiveChatId,
      subtype: 'connecting',
      timestamp: Date.now(),
    });
    const url = this.buildUrl();
    const ws = new this.WSCtor(url);
    this.ws = ws;
    ws.addEventListener('open', () => {
      // attempts só zera quando ChatEvent connected chegar (backend confirma upstream)
    });
    ws.addEventListener('message', (e: MessageEvent) => {
      if (this.terminated) return;
      let event: ChatEvent | null = null;
      try {
        event = JSON.parse(String(e.data)) as ChatEvent;
      } catch {
        return;
      }
      this.store.getState().push(event);
      if (event.type === 'connection' && event.subtype === 'connected') {
        this.attempts = 0;
      }
      if (event.type === 'connection' && event.subtype === 'disconnected' && event.reason === 'live_ended') {
        this.terminated = true;
      }
    });
    ws.addEventListener('close', (e: CloseEvent) => {
      this.ws = null;
      if (this.terminated) return;
      const retryInMs = delayFor(this.attempts);
      this.attempts = Math.min(this.attempts + 1, MAX_ATTEMPTS);

      // Trata close code 4002 owner_rotated_<platform> com prefix-match (Lote D)
      const closeReason = (e as { reason?: string }).reason ?? '';
      const isOwnerRotated = closeReason.startsWith('owner_rotated');
      if (isOwnerRotated) {
        // Propaga o reason bruto pro store de forma observável em teste
        this.store.getState().setLastReason(closeReason);
      }

      this.store.getState().push({
        type: 'connection',
        platform: this.activePlatform,
        liveChatId: this.activeLiveChatId,
        subtype: 'reconnecting',
        attempt: this.attempts,
        retryInMs,
        timestamp: Date.now(),
      });
      this.retryTimer = setTimeout(() => {
        this.retryTimer = null;
        this.connect();
      }, retryInMs);
    });
  }

  disconnect(): void {
    this.terminated = true;
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    try { this.ws?.close(1000); } catch { /* ignore */ }
    this.ws = null;
  }
}
